import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { toast } from 'sonner';
import Honeybadger from '@honeybadger-io/js';

import { deserializer } from '../../../../../shared/utils';
import { INFINITE_SCROLL_PER_PAGE } from '../../../../shared/constants';
import { ERROR_TITLES } from '../../constants';
import { useFilters } from '../../hooks';
import { fetchTasks as apiFetchTasks } from '../../services/taskService';
import { fetchTaskStatuses as apiFetchTaskStatuses } from '../../services/taskStatusService';
import BoardContext from './BoardContext';

const INITIAL_PAGINATION = () => ({
  page: 1,
  hasMore: false,
  isLoadingMore: false,
  totalTasks: 0,
});

// For sorting statuses by their :sortOrder attributes.
const bySortOrder = (a, b) => a.status.sortOrder - b.status.sortOrder;

const BoardProvider = ({ children, searchQuery }) => {
  // :board takes the following shape:
  // { [statusId]: { status: {...}, tasks: [...], pagination: {...} } }
  const [board, setBoard] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { activeFilters } = useFilters();

  const statuses = useMemo(
    () =>
      Object.values(board)
        .sort(bySortOrder)
        .map(({ status }) => status),
    [board]
  );

  const tasks = useMemo(
    () => Object.values(board).flatMap(({ tasks }) => tasks),
    [board]
  );

  const addStatus = useCallback((status) => {
    setBoard((prev) => ({
      ...prev,
      [status.id]: { status, tasks: [], pagination: INITIAL_PAGINATION },
    }));
  }, []);

  const removeStatus = useCallback((status) => {
    setBoard(({ [status.id]: _, ...rest }) => rest);
  }, []);

  const updateStatus = useCallback((status) => {
    setBoard((prev) => ({
      ...prev,
      [status.id]: { ...prev[status.id], status },
    }));
  }, []);

  const addTask = useCallback((task) => {
    const statusId = task.taskStatus?.id;

    if (!statusId) return;

    setBoard((prev) => ({
      ...prev,
      [statusId]: {
        ...prev[statusId],
        tasks: [...prev[statusId].tasks, task],
        pagination: {
          ...prev[statusId].pagination,
          totalTasks: prev[statusId].pagination.totalTasks + 1,
        },
      },
    }));
  }, []);

  const removeTask = useCallback((task) => {
    const statusId = task.taskStatus?.id;

    if (!statusId) return;

    setBoard((prev) => ({
      ...prev,
      [statusId]: {
        ...prev[statusId],
        tasks: prev[statusId].tasks.filter((t) => t.id !== task.id),
        pagination: {
          ...prev[statusId].pagination,
          totalTasks: prev[statusId].pagination.totalTasks - 1,
        },
      },
    }));
  }, []);

  const updateTask = useCallback((task) => {
    setBoard((prev) => {
      const updatedBoard = {};
      const updatedStatusId = task.taskStatus?.id;

      for (const [statusId, statusData] of Object.entries(prev)) {
        const withoutTask = statusData.tasks.filter((t) => t.id !== task.id);
        const losesTask = withoutTask.length < statusData.tasks.length;
        const receivesTask = statusId === updatedStatusId;

        updatedBoard[statusId] = {
          ...statusData,
          tasks: receivesTask ? [...withoutTask, task] : withoutTask,
          pagination: {
            ...statusData.pagination,
            totalTasks:
              statusData.pagination.totalTasks +
              (receivesTask ? 1 : 0) -
              (losesTask ? 1 : 0),
          },
        };
      }

      return updatedBoard;
    });
  }, []);

  const updatePagination = useCallback((statusId, paginationUpdates) => {
    setBoard((prev) => ({
      ...prev,
      [statusId]: {
        ...prev[statusId],
        pagination: { ...prev[statusId].pagination, ...paginationUpdates },
      },
    }));
  }, []);

  const fetchMoreTasks = useCallback(
    async (statusId) => {
      const statusGroup = board[statusId];

      if (!statusGroup.pagination.hasMore || statusGroup.pagination.isLoadingMore) return;

      const nextPage = statusGroup.pagination.page + 1;

      updatePagination(statusId, { isLoadingMore: true });

      try {
        const response = await apiFetchTasks({
          activeFilters,
          page: nextPage,
          perPage: INFINITE_SCROLL_PER_PAGE,
          searchQuery,
          taskStatusId: statusId,
        });

        const newTasks = await deserializer.deserialize(response);
        const newTaskCount = response.meta.pagination.records ?? 0;

        setBoard((prev) => {
          const currStatusGroup = prev[statusId];
          const updatedTasks = [...currStatusGroup.tasks, ...newTasks];

          return {
            ...prev,
            [statusId]: {
              ...currStatusGroup,
              tasks: updatedTasks,
              pagination: {
                page: nextPage,
                hasMore: updatedTasks.length < newTaskCount,
                isLoadingMore: false,
                totalTasks: newTaskCount,
              },
            },
          };
        });
      } catch (error) {
        updatePagination(statusId, { isLoadingMore: false });
        toast.error(ERROR_TITLES.workflow.fetch, { description: error.message });
        Honeybadger.notify(error);
      }
    },
    [activeFilters, board, searchQuery, updatePagination]
  );

  useEffect(() => {
    const fetchBoard = async () => {
      setIsLoading(true);

      try {
        const taskStatuses = await apiFetchTaskStatuses();
        const deserializedStatuses = await deserializer.deserialize(taskStatuses);

        const tasksByStatus = await Promise.all(
          deserializedStatuses.map(async (status) => {
            const response = await apiFetchTasks({
              activeFilters,
              page: 1,
              perPage: INFINITE_SCROLL_PER_PAGE,
              searchQuery,
              taskStatusId: status.id,
            });

            return {
              tasks: await deserializer.deserialize(response),
              totalTasks: response.meta.pagination.records ?? 0,
            };
          })
        );

        const newBoard = {};

        deserializedStatuses.forEach((status, idx) => {
          const { tasks, totalTasks } = tasksByStatus[idx];

          newBoard[status.id] = {
            status,
            tasks,
            pagination: {
              hasMore: tasks.length < totalTasks,
              isLoadingMore: false,
              page: 1,
              totalTasks,
            },
          };
        });

        setBoard(newBoard);
      } catch (error) {
        toast.error(ERROR_TITLES.workflow.fetch, { description: error.message });
        Honeybadger.notify(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBoard();
  }, [activeFilters, searchQuery]);

  const contextValue = useMemo(
    () => ({
      addStatus,
      addTask,
      board,
      fetchMoreTasks,
      isLoading,
      removeStatus,
      removeTask,
      statuses,
      tasks,
      updateStatus,
      updateTask,
    }),
    [
      addStatus,
      addTask,
      board,
      fetchMoreTasks,
      isLoading,
      removeStatus,
      removeTask,
      statuses,
      tasks,
      updateStatus,
      updateTask,
    ]
  );

  return <BoardContext.Provider value={contextValue}>{children}</BoardContext.Provider>;
};

BoardProvider.propTypes = {
  children: PropTypes.node.isRequired,
  searchQuery: PropTypes.string.isRequired,
};

export default BoardProvider;
